import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Company, DataConnection } from '@lendica/utils';
import { useAccountApi } from './api';

interface AccountContextInterface {
    isLoading: boolean;
    data: {
        company: Company;
        dataConnections: DataConnection[];
    };
    getSummary?: () => Promise<void>;
}

const defaultState = {
    isLoading: true,
    data: {
        company: {} as Company,
        dataConnections: [] as DataConnection[],
    },
};

const AccountDataContext = createContext<AccountContextInterface>(defaultState);

export const useAccountDataContext = () => {
    return useContext(AccountDataContext);
};

export const AccountData = ({ children }: { children: ReactNode }) => {
    const [state, setState] = useState(defaultState);
    const api = useAccountApi();

    useEffect(() => {
        let cancelled = false;
        let timeoutId: any;
        (async () => {
            setState(currentState => ({
                ...currentState,
                isLoading: true,
                data: { company: {} as Company, dataConnections: [] as DataConnection[] },
            }));
            try {
                const company = await api.getCompanyDetail();
                if (cancelled) return;
                setState(currentState => ({
                    ...currentState,
                    data: { ...currentState.data, company },
                }));
                const dataConnections = await api.getConnections();
                if (cancelled) return;
                setState(currentState => ({
                    ...currentState,
                    data: { ...currentState.data, dataConnections },
                }));
            } catch {
            } finally {
                timeoutId = setTimeout(() => {
                    if (cancelled) return;
                    setState(currentState => ({ ...currentState, isLoading: false }));
                }, 500);
            }
        })();
        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [api]);

    return (
        <AccountDataContext.Provider
            value={{
                ...state,
            }}
        >
            {children}
        </AccountDataContext.Provider>
    );
};
